/**
 * mcpbTimeline.js — pure geometry/labels for the MCPlayback composite timeline (Phase 1f).
 *
 * The timeline lane spans the committed search window [windowStart, +windowLen]. Clip rects + AI dots
 * are positioned as a PERCENT of that span (left/width), so the DOM scales with the lane width without
 * re-measuring. All time math is whole-second + DST-immune (util/mcpbClips + util/mcpbTime).
 *
 * DOM (not canvas) per the build-tracker §E decision (maintainability; the mock proves DOM is legible;
 * windowing is the perf escape hatch at 24h, not canvas). v1 cache-state is flat (the cached/`--*-cached`
 * coloring is a v2 hook); smart-fill is desaturated when fSmartFill is set.
 */
import { clipStartOffset, clipDurationSec } from './mcpbClips';
import { addSeconds, chipTime, secondsOfDay, shortDate } from './mcpbTime';

/**
 * Clip rect as { left, width } percent of the window [0..100], CLAMPED to the visible span (a clip
 * that begins before / ends after the window is trimmed to the edge). null when the clip is entirely
 * outside the window or the window has no length.
 */
export const clipRectPct = (clip, windowStartTs, windowLenSec) => {
  if (!windowLenSec || windowLenSec <= 0) return null;
  const start = clipStartOffset(clip, windowStartTs);
  if (!Number.isFinite(start)) return null;
  const end = start + clipDurationSec(clip);
  const lo = Math.max(0, start);
  const hi = Math.min(windowLenSec, end);
  if (hi <= 0 || lo >= windowLenSec || hi <= lo) return null;
  return { left: (lo / windowLenSec) * 100, width: ((hi - lo) / windowLenSec) * 100 };
};

// Recognized AI marker vocabularies. The frozen event's sTags is a free-form string (the aiengine /
// Java MCEventModel write it); we classify into the three v1 marker types. The exact tag strings on
// AI-tagged clips should be confirmed against aiengine output when such data is available — unknown
// tags are ignored (graceful: no dot), never guessed into a wrong color.
const TAG_MAP = [
  { type: 'person', re: /\b(person|people|ped|pedestrian|human)\b/i },
  { type: 'vehicle', re: /\b(vehicle|car|truck|van|motorcycle|bus|auto)\b/i },
  { type: 'alert', re: /\b(alert|alarm|intrusion|loiter|tamper)\b/i },
];

/**
 * Parse sTags into recognized AI marker types (person/vehicle/alert), deduped, in TAG_MAP order
 * (the first → the dot color, inventory §7). Empty/non-string/unrecognized → [].
 */
export const parseAiTags = (sTags) => {
  if (!sTags || typeof sTags !== 'string') return [];
  const out = [];
  TAG_MAP.forEach(({ type, re }) => { if (re.test(sTags) && !out.includes(type)) out.push(type); });
  return out;
};

// Clock-aligned tick intervals (seconds), smallest -> largest: 1m, 2m, 5m, 10m, 15m, 30m, 1h, 2h, 4h,
// 6h, 12h, 24h. adaptiveTicks picks the smallest that keeps the label count sane for the span.
const TICK_STEPS = [60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400];
const MAX_TICK_INTERVALS = 6; // aim for <= ~6 gaps across the window (so ~4-7 labels)

/**
 * P-N4 — clock-aligned, span-adaptive ruler ticks. Returns `[{ pct, label }]` where `pct` is the tick's
 * position across the window [0..100] and `label` is 'HH:MM'. Unlike the old evenly-spaced tickLabels
 * (which labeled fractions FROM the window start), each tick is snapped to a CLOCK boundary (a multiple
 * of the chosen interval within the day), so a window that starts at 17:07 still shows 17:10 / 17:20 / …
 * — matching Java MCTimelineTicks. The interval scales with the span (1-min ticks at 5-min zoom up to
 * 4-hour ticks at 24-hour zoom). Empty for a missing/zero window.
 */
export const adaptiveTicks = (windowStartTs, windowLenSec) => {
  if (!windowStartTs || !windowLenSec || windowLenSec <= 0) return [];
  const step = TICK_STEPS.find((s) => windowLenSec / s <= MAX_TICK_INTERVALS)
    || TICK_STEPS[TICK_STEPS.length - 1];
  // Seconds from the window start to the first clock-aligned tick (0 when the start is already aligned).
  const startSod = secondsOfDay(windowStartTs);
  const firstOff = (step - (startSod % step)) % step;
  const out = [];
  for (let off = firstOff; off <= windowLenSec + 0.5; off += step) {
    out.push({ pct: (off / windowLenSec) * 100, label: chipTime(addSeconds(windowStartTs, off)).slice(0, 5) });
  }
  return out;
};

/**
 * P-N3 — playhead time-pill text: 'MMM D HH:MM:SS' at the playhead's absolute time (window start +
 * offset), e.g. 'Jul 29 13:05:07'. Empty when there is no window.
 */
export const playheadPillLabel = (windowStartTs, offsetSec = 0) => {
  if (!windowStartTs) return '';
  const ts = addSeconds(windowStartTs, Math.max(0, Math.round(Number(offsetSec) || 0)));
  return `${shortDate(ts)} ${chipTime(ts)}`.trim();
};

/** N evenly-spaced 'HH:MM' tick labels across the window (inclusive of both ends; n >= 2). */
export const tickLabels = (windowStartTs, windowLenSec, n = 5) => {
  if (!windowStartTs || !windowLenSec || windowLenSec <= 0 || n < 2) return [];
  const out = [];
  for (let i = 0; i < n; i++) {
    const sec = Math.round((windowLenSec * i) / (n - 1));
    out.push(chipTime(addSeconds(windowStartTs, sec)).slice(0, 5)); // HH:MM
  }
  return out;
};

/**
 * Edge-safe position for an absolutely-positioned, CENTER-anchored label (used with transform:
 * translateX(-50%)). Clamps the label's center to stay at least `insetPx` from each edge of its lane,
 * so a label up to `2*insetPx` wide never runs off the page — it "sticks" flush at the edge instead.
 * Replaces the old 3-way edgeAnchor flush (which only kicked in at <=1%/>=99%, far too tight for the
 * ~100px date-time pill) on the mobile union band (David 2026-08-04). CSS clamp(): `insetPx` is the
 * lower bound, `pct%` the ideal center, `calc(100% - insetPx)` the upper bound.
 */
export const edgeClampLeft = (pct, insetPx) => `clamp(${insetPx}px, ${pct}%, calc(100% - ${insetPx}px))`;

/** Seek offset (whole seconds) from a bar-click fraction [0..1], clamped to [0, windowLen]. */
export const offsetFromFraction = (fraction, windowLenSec) =>
  Math.max(0, Math.min(windowLenSec, Math.round((Number(fraction) || 0) * windowLenSec)));
